import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { Button, Flex, Modal } from '@mantine/core';

import { usePatchOrganisationBoard } from 'api/organisations';
import { Form, TextField } from 'components/shared';
import { useShowNotification } from 'hooks';
import type { BoardType } from 'types';

type FormData = {
  name: string;
};

type Props = {
  opened: boolean;
  onClose: () => void;
  board: BoardType;
};

export const NameEditModal = ({ opened, onClose, board }: Props) => {
  const { organisationId, boardId } = useParams();

  if (!organisationId || !boardId) throw new Error('Missing URL params');

  const { t } = useTranslation('organisations');
  const form = useForm<FormData>({
    defaultValues: { name: board.name ?? undefined },
  });
  const {
    formState: { isSubmitting },
  } = form;
  const { mutateAsync: patchOrganisationBoard } = usePatchOrganisationBoard();
  const showNotification = useShowNotification();

  const onSubmit = async (data: FormData) => {
    try {
      await patchOrganisationBoard({
        pathParams: { organisationId, boardId },
        body: data,
      });
      showNotification({
        variant: 'success',
        message: t(
          'organisationView.boards.generalSettings.profile.name.successMessage',
        ),
      });
      onClose();
    } catch (error) {
      showNotification({
        variant: 'error',
        message: t(
          'organisationView.boards.generalSettings.profile.name.errorMessage',
        ),
      });
    }
  };

  useEffect(() => {
    form.reset({ name: board.name });
  }, [board.name]);

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={t(
        'organisationView.boards.generalSettings.profile.name.modal.title',
      )}
      centered
      size="md"
    >
      <Form form={form} onSubmit={onSubmit}>
        <TextField name="name" withErrorWrapperAlwaysEnabled={false} />
        <Flex justify="flex-end" gap="lg" mt="lg">
          <Button variant="default" onClick={onClose}>
            {t(
              'organisationView.boards.generalSettings.profile.name.modal.cancelButton',
            )}
          </Button>
          <Button type="submit" loading={isSubmitting}>
            {t(
              'organisationView.boards.generalSettings.profile.name.modal.saveButton',
            )}
          </Button>
        </Flex>
      </Form>
    </Modal>
  );
};
